/**
 * Sensible defaults so the tool produces meaningful output before the user
 * enters anything (spec §21: "Ship sensible defaults everywhere").
 */
import { CURRENT_SCHEMA_VERSION, type AssumptionSet, type Plan } from './types';

/** Stable id generator that does not depend on Math.random / Date (test-safe). */
let _idCounter = 0;
export function newId(prefix = 'id'): string {
  _idCounter += 1;
  return `${prefix}_${_idCounter.toString(36)}`;
}
export function resetIdCounter(): void {
  _idCounter = 0;
}

export const CURRENT_YEAR = 2026;

// The three assumption profiles (spec §5). Seeded, all fields editable.
export const OPTIMISTIC: AssumptionSet = {
  label: 'optimistic',
  returnsByAssetClass: { equity: 0.09, bond: 0.045, cash: 0.03 },
  generalInflation: 0.022,
  healthcareInflation: 0.045,
  ltcInflation: 0.05,
  wageGrowth: 0.04,
  housingAppreciation: 0.04,
  longevityAge: 95,
};

export const AVERAGE: AssumptionSet = {
  label: 'average',
  returnsByAssetClass: { equity: 0.07, bond: 0.04, cash: 0.025 },
  generalInflation: 0.026,
  healthcareInflation: 0.055,
  ltcInflation: 0.06,
  wageGrowth: 0.03,
  housingAppreciation: 0.03,
  longevityAge: 92,
};

export const PESSIMISTIC: AssumptionSet = {
  label: 'pessimistic',
  returnsByAssetClass: { equity: 0.05, bond: 0.03, cash: 0.02 },
  generalInflation: 0.032,
  healthcareInflation: 0.065,
  ltcInflation: 0.07,
  wageGrowth: 0.02,
  housingAppreciation: 0.02,
  // Default the pessimistic profile to apply the SS haircut (spec §5).
  // Labeled in the UI as a policy assumption, not a forecast.
  socialSecurityHaircut: { startYear: 2033, payableFraction: 0.77 },
  longevityAge: 90,
};

export function defaultAssumptionProfiles(): AssumptionSet[] {
  return [
    structuredClone(OPTIMISTIC),
    structuredClone(AVERAGE),
    structuredClone(PESSIMISTIC),
  ];
}

/**
 * A realistic single-person starter plan. Not a real person — placeholder
 * numbers chosen to exercise the engine and show a working projection.
 */
export function defaultPlan(): Plan {
  resetIdCounter();
  const personId = newId('person');

  return {
    schemaVersion: CURRENT_SCHEMA_VERSION,
    household: {
      people: [
        {
          id: personId,
          label: 'You',
          birthYear: 1971, // age 55 in 2026
          birthMonth: 6,
          retirementAge: 65,
          lifeExpectancyAge: 92,
          socialSecurity: {
            estimatedMonthlyAtFRA: 2800,
            claimingAge: 67,
          },
        },
      ],
      filingStatus: 'single',
      dependents: [],
      stateOfResidence: 'TX',
      relocations: [],
    },
    accounts: [
      {
        id: newId('acct'),
        ownerId: personId,
        type: 'trad_401k',
        label: '401(k)',
        balance: 650_000,
        asOfDate: `${CURRENT_YEAR}-01-01`,
        allocation: { equityPct: 60, bondPct: 35, cashPct: 5 },
        contributions: { employee: 23_500, endAge: 65 },
      },
      {
        id: newId('acct'),
        ownerId: personId,
        type: 'roth_ira',
        label: 'Roth IRA',
        balance: 120_000,
        asOfDate: `${CURRENT_YEAR}-01-01`,
        allocation: { equityPct: 80, bondPct: 15, cashPct: 5 },
      },
      {
        id: newId('acct'),
        ownerId: personId,
        type: 'brokerage',
        label: 'Brokerage',
        balance: 200_000,
        costBasis: 140_000,
        asOfDate: `${CURRENT_YEAR}-01-01`,
        allocation: { equityPct: 70, bondPct: 20, cashPct: 10 },
      },
      {
        id: newId('acct'),
        ownerId: personId,
        type: 'checking_savings',
        label: 'Cash',
        balance: 40_000,
        costBasis: 40_000,
        asOfDate: `${CURRENT_YEAR}-01-01`,
        allocation: { equityPct: 0, bondPct: 0, cashPct: 100 },
      },
    ],
    properties: [],
    incomes: [
      {
        id: newId('inc'),
        ownerId: personId,
        label: 'Salary',
        type: 'w2',
        annualAmount: 120_000,
        startAge: 55,
        endAge: 65,
        taxable: true,
        taxTreatment: 'ordinary',
      },
    ],
    budget: {
      mode: 'simple',
      annualAmount: 70_000,
      retirementSpendingMultiplier: 0.9,
      essentialFraction: 0.65,
    },
    debts: [],
    oneTimeEvents: [],
    assumptionProfiles: defaultAssumptionProfiles(),
    activeAssumptionLabel: 'average',
    decisions: {
      withdrawalStrategy: 'fund_spending',
      orderMode: 'traditional',
    },
  };
}

/**
 * The plan the app starts with: every dollar figure is zero, so nothing on
 * screen is ever mistaken for the user's own money. Only the structural
 * fields a projection cannot run without — an age, a retirement age, a
 * horizon, a filing status — carry neutral starting values, and each is
 * plainly editable.
 */
export function emptyPlan(): Plan {
  resetIdCounter();
  const personId = newId('person');
  return {
    schemaVersion: CURRENT_SCHEMA_VERSION,
    household: {
      people: [
        {
          id: personId,
          label: 'You',
          birthYear: CURRENT_YEAR - 50,
          birthMonth: 1,
          retirementAge: 65,
          lifeExpectancyAge: 92,
          socialSecurity: { estimatedMonthlyAtFRA: 0, claimingAge: 67 },
        },
      ],
      filingStatus: 'single',
      dependents: [],
      stateOfResidence: 'TX',
      relocations: [],
    },
    accounts: [],
    properties: [],
    incomes: [],
    budget: { mode: 'simple', annualAmount: 0, retirementSpendingMultiplier: 1, essentialFraction: 0.65 },
    debts: [],
    oneTimeEvents: [],
    // Healthcare is left to DEFAULT_HEALTHCARE's typical figures rather than
    // zeroed: unlike a balance, an omitted medical cost silently understates
    // the plan, and before 65 it is usually the largest expense of all. The
    // blank-plan guard in the projection keeps a pristine plan at zero anyway,
    // so these only start applying once real numbers are entered.
    assumptionProfiles: defaultAssumptionProfiles(),
    activeAssumptionLabel: 'average',
    decisions: { withdrawalStrategy: 'fund_spending', orderMode: 'traditional' },
  };
}
